'use client'

import Image from 'next/image'
import Link from 'next/link'
import { RateCtaLabel, StarRating } from '@/components/star-rating'
import { EntityTrendChart } from '@/components/entity-trend-chart'

interface EntitySummary {
  currentRating: number
  totalRatings: number
  entity: {
    id: string
    slug: string
    name: string
    description: string | null
    imageUrl: string | null
    type: string
    parent: {
      name: string
      parent: {
        name: string
      } | null
    } | null
  }
}

interface BestOfListProps {
  entities: EntitySummary[]
  typeSlug: string
  typeTitle: string
}

export function BestOfList({ entities, typeSlug, typeTitle }: BestOfListProps) {
  const getLocationString = (entity: EntitySummary['entity']) => {
    const parts: string[] = []
    if (entity.parent?.name) parts.push(entity.parent.name)
    if (entity.parent?.parent?.name) parts.push(entity.parent.parent.name)
    return parts.join(' • ')
  }

  // Always sort by rating and limit to 5
  const topEntities = [...entities]
    .sort((a, b) => b.currentRating - a.currentRating)
    .slice(0, 5)

  return (
    <div>
      {/* Entity List */}
      {topEntities.length > 0 ? (
        <div className="space-y-6">
          {topEntities.map((summary, index) => (
            <Link
              key={summary.entity.id}
              href={`/rate/${summary.entity.slug}`}
              className="block bg-white border border-slate-200 rounded-lg shadow-sm hover:border-[#1E4289] hover:shadow-md transition-all overflow-hidden group"
            >
              <div className="flex flex-col md:flex-row">
                {/* Rank Badge */}
                <div className="flex-shrink-0 flex items-center justify-center w-full md:w-24 h-16 md:h-auto bg-gradient-to-br from-[#1E4289] to-[#0f2654]">
                  <span className="text-4xl font-bold text-[#FEDB1A]">#{index + 1}</span>
                </div>

                {/* Image */}
                <div className="relative w-full md:w-48 h-48 md:h-auto flex-shrink-0 bg-slate-100">
                  <Image
                    src={summary.entity.imageUrl || '/no_image.png'}
                    alt={summary.entity.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-300"
                    unoptimized
                  />
                </div>

                {/* Content */}
                <div className="flex-1 p-6">
                  <h2 className="text-xl font-bold text-slate-800 mb-2 group-hover:text-[#1E4289] transition-colors">
                    {summary.entity.name}
                  </h2>

                  {/* Location */}
                  {getLocationString(summary.entity) && (
                    <p className="text-sm text-slate-500 mb-3">
                      {getLocationString(summary.entity)}
                    </p>
                  )}

                  {/* Rating */}
                  <div className="mb-3">
                    <div className="flex items-center gap-4 mb-3 flex-wrap">
                      <StarRating rating={summary.currentRating} size="lg" showNumber />
                      <span className="text-sm text-slate-500">
                        {summary.totalRatings} {summary.totalRatings === 1 ? 'rating' : 'ratings'}
                      </span>
                    </div>
                    <RateCtaLabel />
                  </div>

                  {/* Trend Chart */}
                  <div className="mb-3">
                    <p className="text-xs text-slate-500 mb-1">Rating Trend</p>
                    <EntityTrendChart
                      entityId={summary.entity.id}
                      height={50}
                    />
                  </div>

                  {/* Description */}
                  {summary.entity.description && (
                    <p className="text-slate-600 text-sm leading-relaxed">
                      {summary.entity.description.length > 150
                        ? `${summary.entity.description.slice(0, 150)}...`
                        : summary.entity.description}
                    </p>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg shadow-sm">
          <p className="text-slate-600 text-lg">No rated {typeSlug} yet.</p>
          <p className="text-slate-500 mt-2">Be the first to rate!</p>
          <Link
            href={`/rate?type=${typeSlug.toUpperCase()}`}
            className="inline-block mt-6 px-6 py-3 bg-[#1E4289] text-white font-semibold rounded hover:bg-[#FEDB1A] hover:text-[#1E4289] transition-colors"
          >
            Start Rating
          </Link>
        </div>
      )}
    </div>
  )
}

